"use strict";
/**
 * Money primitives.
 *
 * Rule: every amount in the pricing path is an integer number of cents. Dollars
 * exist only at the two edges — parsing human input, and rendering a string for
 * a human to read. Nothing in between is a float, because `58.95 * 3` is
 * `176.85000000000002` and `Math.round(167.42 * 8.875 / 100)` overcharges by
 * fourteen cents.
 *
 * Cents are also Stripe's native unit for USD, so the value we compute is the
 * value we charge with no conversion step left to get wrong.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.readMoneyColumnToCents = exports.toStripeAmount = exports.percentOfCents = exports.percentToBasisPoints = exports.percentDiscountCents = exports.applyRate = exports.sumCents = exports.formatCents = exports.parseMoneyToCents = exports.centsToDollars = exports.dollarsToCents = exports.assertCents = exports.roundHalfAwayFromZero = void 0;
const MAX_CENTS = Number.MAX_SAFE_INTEGER;
/**
 * Round half away from zero, the convention customers and accountants expect.
 * `Math.round` alone rounds -2.5 to -2, which makes a refund disagree with the
 * charge it reverses.
 */
function roundHalfAwayFromZero(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
exports.roundHalfAwayFromZero = roundHalfAwayFromZero;
/** Throws unless `value` is a safe integer count of cents. */
function assertCents(value, label = "amount") {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new RangeError(`${label} must be a finite number, got ${String(value)}`);
    }
    if (!Number.isInteger(value)) {
        throw new RangeError(`${label} must be an integer number of cents, got ${value}. ` +
            `Use dollarsToCents() at the boundary instead of passing dollars.`);
    }
    if (Math.abs(value) > MAX_CENTS) {
        throw new RangeError(`${label} exceeds the safe integer range: ${value}`);
    }
    return value;
}
exports.assertCents = assertCents;
/**
 * Convert dollars to cents.
 *
 * The `toFixed(4)` step is load-bearing: `1.005 * 100` is `100.49999999999999`,
 * which would round down and lose the half cent.
 */
function dollarsToCents(dollars) {
    if (typeof dollars !== "number" || !Number.isFinite(dollars)) {
        throw new RangeError(`Cannot convert ${String(dollars)} to cents`);
    }
    return roundHalfAwayFromZero(Number((dollars * 100).toFixed(4)));
}
exports.dollarsToCents = dollarsToCents;
/**
 * Convert cents to a dollar number. Lossy by nature — use for display or for
 * interop with legacy dollar-based code, never as an intermediate step.
 */
function centsToDollars(cents) {
    return assertCents(cents) / 100;
}
exports.centsToDollars = centsToDollars;
/**
 * Parse money entered by a human or received over the wire ("$1,234.56", 58.95).
 *
 * Rejects more than two decimal places rather than rounding, so an admin who
 * types 58.955 finds out immediately instead of discovering later that we
 * stored 58.96 and quoted something else.
 */
function parseMoneyToCents(input) {
    if (typeof input === "number") {
        const scaled = input * 100;
        if (Math.abs(scaled - Math.round(scaled)) > 1e-6) {
            throw new RangeError(`${input} has more precision than cents allow (max 2 decimal places)`);
        }
        return dollarsToCents(input);
    }
    const cleaned = String(input).trim().replace(/[$,\s]/g, "");
    if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned)) {
        throw new RangeError(`"${input}" is not a valid money amount with at most 2 decimal places`);
    }
    return dollarsToCents(Number(cleaned));
}
exports.parseMoneyToCents = parseMoneyToCents;
/**
 * Render cents for display. Built from integer parts rather than
 * `Intl.NumberFormat(cents / 100)` so the output can never be
 * `$167.41999999999998` or drop a trailing zero to `$167.4`.
 */
function formatCents(cents) {
    assertCents(cents);
    const sign = cents < 0 ? "-" : "";
    const abs = Math.abs(cents);
    const dollars = Math.floor(abs / 100);
    const remainder = abs % 100;
    return `${sign}$${dollars.toLocaleString("en-US")}.${String(remainder).padStart(2, "0")}`;
}
exports.formatCents = formatCents;
/** Sum cent amounts with no float drift. */
function sumCents(amounts) {
    return amounts.reduce((total, amount) => total + assertCents(amount, "line item"), 0);
}
exports.sumCents = sumCents;
/**
 * Apply a rate using exact integer arithmetic, rounding half away from zero.
 * Never divides floats, so an exact `.5` remainder resolves deterministically
 * rather than depending on binary representation.
 */
function applyRate(cents, rate) {
    assertCents(cents);
    const negative = cents < 0;
    const scaled = Math.abs(cents) * rate.numerator;
    if (!Number.isSafeInteger(scaled)) {
        throw new RangeError(`Rate application overflows safe integers: ${scaled}`);
    }
    const quotient = Math.floor(scaled / rate.denominator);
    const remainder = scaled - quotient * rate.denominator;
    const rounded = remainder * 2 >= rate.denominator ? quotient + 1 : quotient;
    return negative ? -rounded : rounded;
}
exports.applyRate = applyRate;
/** A whole-percent discount, in cents. */
function percentDiscountCents(cents, percent) {
    return applyRate(cents, { numerator: percent, denominator: 100 });
}
exports.percentDiscountCents = percentDiscountCents;
/**
 * Convert an admin-entered percentage to exact basis points.
 *
 * Admin-configurable surge is typed by a human, and a human will type 12.5%.
 * Two decimal places is the limit, for the same reason `parseMoneyToCents`
 * rejects a third: an admin who types 12.555 should find out now rather than
 * discover later that we stored 12.56 and charged something else.
 */
function percentToBasisPoints(percent, label = "percentage") {
    if (typeof percent !== "number" || !Number.isFinite(percent)) {
        throw new RangeError(`${label} must be a finite number, got ${String(percent)}`);
    }
    const scaled = percent * 100;
    if (Math.abs(scaled - Math.round(scaled)) > 1e-6) {
        throw new RangeError(`${label} ${percent} has more precision than two decimal places allow`);
    }
    return Math.round(scaled);
}
exports.percentToBasisPoints = percentToBasisPoints;
/**
 * A percentage of an amount, in cents, where the percentage itself may carry up
 * to two decimal places.
 *
 * Use this rather than `percentDiscountCents` for any rate an admin can type.
 * That function puts `percent` straight into a rate's numerator, so a fractional
 * percentage survives only while `cents * percent` happens to land on an integer:
 * 12.5% of $208.00 is fine, 12.5% of $0.99 throws on `applyRate`'s safe-integer
 * guard. Working correctly on most bases and failing on the awkward one is worse
 * than failing outright. Scaling to basis points first makes every step exact
 * integer arithmetic on any base — the same reason `TAX_RATE` is a rational and
 * never the float 0.08875.
 */
function percentOfCents(cents, percent, label = "percentage") {
    return applyRate(cents, {
        numerator: percentToBasisPoints(percent, label),
        denominator: 10000,
    });
}
exports.percentOfCents = percentOfCents;
/**
 * The amount to hand Stripe. For USD this is the identity function — cents are
 * already Stripe's minor unit — but it asserts the invariant at the boundary so
 * a stray dollar value can never be charged as cents, a 100x undercharge.
 */
function toStripeAmount(cents) {
    return assertCents(cents, "Stripe amount");
}
exports.toStripeAmount = toStripeAmount;
/**
 * Read a money value that may have come back from Postgres as a string.
 *
 * `numeric`/`decimal` columns hydrate as JavaScript strings — node-postgres
 * registers no parser for OID 1700 and TypeORM does not coerce them — so
 * `total + 0` is string concatenation and `total.toFixed(2)` throws. Both
 * failures are silent in this codebase: they happen inside `try/catch` blocks
 * that only log, so a broken receipt looks like no receipt at all.
 */
function readMoneyColumnToCents(value, { unit }) {
    if (value === null || value === undefined)
        return 0;
    const asNumber = typeof value === "string" ? Number(value) : value;
    if (!Number.isFinite(asNumber)) {
        throw new RangeError(`Money column value is not numeric: ${JSON.stringify(value)}`);
    }
    return unit === "cents" ? roundHalfAwayFromZero(asNumber) : dollarsToCents(asNumber);
}
exports.readMoneyColumnToCents = readMoneyColumnToCents;
//# sourceMappingURL=money.js.map